import "./EditorLeftMenu.css";
import {
  newImageIcon,
  backgroundIcon,
  resizeIcon,
  aiUpscaleIcon,
  aiEnhanceIcon,
  aiRelightIcon,
  moreIcon,
  layersIcon,
  uploadIcon,
} from "../../icons/assets";

import { useState, useEffect } from "react";
import { useEditorStore } from "../../state/editorStore";

const AI_MENU_INDICES = new Set([4, 5, 6]);
const PASSPORT_MENU_INDEX = 6;

const EditorLeftMenu = ({
  uploadedImage,
  activeMenuIndex,
  setActiveMenuIndex,
  onAiMenuBlocked = null,
  currentActiveItem,
  menuMode = "standard",
  hideMoreMenu = true,
  hideAiMenus = false,
  hideSecondaryAiMenus = false,
  hideResizeMenu = false,
}) => {
  const isPassportOnly = menuMode === "passport_only";
  const defaultIndex = isPassportOnly ? PASSPORT_MENU_INDEX : 0;
  const [activeIndex, setActiveIndex] = useState(
    isPassportOnly ? PASSPORT_MENU_INDEX : (activeMenuIndex ?? 0)
  );
  // Match Layers / Object panel: selected image overlay name, else Object layer.
  const subjectMenuLabel = useEditorStore((s) => {
    const layers = s.layers || [];
    const selectedId = s.selectedLayerId;
    if (
      selectedId &&
      selectedId !== "object" &&
      selectedId !== "background"
    ) {
      const imageLayer = layers.find(
        (l) => l.id === selectedId && l.type === "image"
      );
      const overlayLabel = imageLayer?.label?.trim();
      if (overlayLabel) return overlayLabel;
    }
    const objectLayer = layers.find((l) => l.id === "object");
    const label = objectLayer?.label?.trim();
    return label && label.toLowerCase() !== "object" ? label : "Object";
  });
  const subjectMenuIcon = useEditorStore((s) => {
    const selectedId = s.selectedLayerId;
    if (
      selectedId &&
      selectedId !== "object" &&
      selectedId !== "background"
    ) {
      const imageLayer = (s.layers || []).find(
        (l) => l.id === selectedId && l.type === "image"
      );
      if (typeof imageLayer?.imageUrl === "string" && imageLayer.imageUrl) {
        return imageLayer.imageUrl;
      }
    }
    return null;
  });

  const trySelectIndex = (index) => {
    if (AI_MENU_INDICES.has(index) && onAiMenuBlocked?.()) {
      return;
    }
    if (activeIndex === index) {
      currentActiveItem?.(index);
      return;
    }
    setActiveIndex(index);
  };

  useEffect(() => {
    if (isPassportOnly) {
      setActiveIndex(PASSPORT_MENU_INDEX);
      return;
    }
    if (activeMenuIndex !== undefined && activeMenuIndex !== activeIndex) {
      setActiveIndex(activeMenuIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only when parent index changes
  }, [activeMenuIndex, isPassportOnly]);

  useEffect(() => {
    // Only push local tab changes up / notify parent — avoid calling the sidebar
    // handler when we just mirrored parent activeMenuIndex (that re-entered selectObject).
    if (activeMenuIndex !== undefined && activeIndex === activeMenuIndex) {
      return;
    }
    currentActiveItem?.(activeIndex);
    if (setActiveMenuIndex && activeIndex !== activeMenuIndex) {
      setActiveMenuIndex(activeIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when local tab changes only
  }, [activeIndex]);

  if (isPassportOnly) {
    return (
      <div className="editor-page__left-menu ">
        <div
          className={`editor-page__left-menu__item ${
            activeIndex === PASSPORT_MENU_INDEX ? "active" : ""
          }`}
          onClick={() => trySelectIndex(PASSPORT_MENU_INDEX)}
        >
          <img
            className="icon icon--subject"
            src={uploadedImage || aiRelightIcon}
            alt="Passport"
          />
          <p>Passport</p>
        </div>
      </div>
    );
  }

  return (
    <div className="editor-page__left-menu ">
      <div
        className={`editor-page__left-menu__item ${
          activeIndex === 0 ? "active" : ""
        }`}
        onClick={() => trySelectIndex(0)}
      >
        <img
          className="icon icon--subject"
          src={subjectMenuIcon || uploadedImage || newImageIcon}
          alt={subjectMenuLabel}
        />
        <p>{subjectMenuLabel}</p>
      </div>
      <div
        className={`editor-page__left-menu__item ${
          activeIndex === 8 ? "active" : ""
        }`}
        onClick={() => trySelectIndex(8)}
      >
        <img className="icon icon--upload" src={uploadIcon} alt="" />
        <p>Upload</p>
      </div>
      <div
        className={`editor-page__left-menu__item ${
          activeIndex === 1 ? "active" : ""
        }`}
        onClick={() => trySelectIndex(1)}
      >
        <img className="icon" src={backgroundIcon} alt="" />
        <p>Background</p>
      </div>
      {!hideResizeMenu && (
        <div
          className={`editor-page__left-menu__item ${
            activeIndex === 2 ? "active" : ""
          }`}
          onClick={() => trySelectIndex(2)}
        >
          <img className="icon" src={resizeIcon} alt="" />
          <p>Resize</p>
        </div>
      )}
      {!hideAiMenus && !hideSecondaryAiMenus && (
        <>
          <div
            className={`editor-page__left-menu__item ${
              activeIndex === 4 ? "active" : ""
            }`}
            onClick={() => trySelectIndex(4)}
          >
            <img className="icon" src={aiEnhanceIcon} alt="" />
            <p>AI Expand</p>
          </div>
          <div
            className={`editor-page__left-menu__item ${
              activeIndex === 5 ? "active" : ""
            }`}
            onClick={() => trySelectIndex(5)}
          >
            <img className="icon" src={aiUpscaleIcon} alt="" />
            <p>AI Upscale</p>
          </div>
        </>
      )}
      <div
        className={`editor-page__left-menu__item ${
          activeIndex === 7 ? "active" : ""
        }`}
        onClick={() => trySelectIndex(7)}
      >
        <img className="icon icon--layers" src={layersIcon} alt="" />
        <p>Layers</p>
      </div>
      {!hideMoreMenu && (
        <div
          className={`editor-page__left-menu__item ${
            activeIndex === 9 ? "active" : ""
          }`}
          onClick={() => setActiveIndex(null)}
        >
          <img className="icon" src={moreIcon} alt="" />
          <p>More</p>
        </div>
      )}
    </div>
  );
};

export default EditorLeftMenu;
